import React, {CSSProperties, FC, ReactNode, useEffect, useMemo} from "react";
import {ThemeProvider} from "@material-ui/styles";
import {BrandingType} from "@evennt-tech/api-v2-core";
import useBranding from "store/branding/branding.store";
import {useGenerateThemeFromBranding} from "view/apps/Exhibitor/helpers";
import {hexToRgba} from "tools/theme/helpers";

type Props = {
  showId: string;
  /**
   * Rendered under the branding `ThemeProvider` but OUTSIDE the branded
   * background div — for a fixed/absolute top bar (e.g. `QuickOrderHeader`)
   * that must not inherit the container margins.
   */
  header?: ReactNode;
  /** Extra styles merged onto the background container (e.g. `marginTop`). */
  containerStyle?: CSSProperties;
  children: ReactNode;
};

/**
 * Shared branding shell for standalone show windows opened via `window.open`
 * (QuickCart, grid preview). Such windows are a fresh SPA mount OUTSIDE the
 * exhibitor/organiser shell, so they never inherit the branding theme the
 * parent window applied. This fetches the show branding, applies the generated
 * theme, and paints the page background from it (COLOR → hex/opacity, IMAGE →
 * uploaded picture darkened by its opacity), keeping the two call sites in sync.
 */
const BrandedShowLayout: FC<Props> = ({
  showId,
  header,
  containerStyle,
  children,
}) => {
  const {getBranding, fetch, autoCatch} = useBranding();
  const branding = getBranding(showId);
  const theme = useGenerateThemeFromBranding(branding);

  useEffect(() => {
    if (showId) {
      fetch(showId);
      autoCatch(showId);
    }
  }, [fetch, autoCatch, showId]);

  const backgroundStyles = useMemo(() => {
    const {r, g, b, a} = hexToRgba(
      branding?.backgroundColor?.hex || "#FFFFFF",
      branding?.backgroundOpacity || 1
    );

    const transformedOpacity = !branding?.backgroundOpacity
      ? 0
      : 1 - branding.backgroundOpacity / 100;
    const opacity = `linear-gradient(rgba(0,0,0,${transformedOpacity}), rgba(0,0,0,${transformedOpacity})),`;
    return {
      backgroundImage:
        branding?.backgroundType === BrandingType.IMAGE
          ? `${opacity} url("${branding?.backgroundImage?.url}")`
          : undefined,
      backgroundColor:
        branding?.backgroundType === BrandingType.COLOR
          ? `rgba(${r}, ${g}, ${b}, ${a})`
          : undefined,
      backgroundSize: "100% 100%",
    };
  }, [
    branding?.backgroundColor?.hex,
    branding?.backgroundImage?.url,
    branding?.backgroundOpacity,
    branding?.backgroundType,
  ]);

  return (
    <ThemeProvider theme={theme}>
      {header}
      <div
        style={{
          ...backgroundStyles,
          minHeight: "100vh",
          width: "100vw",
          // Establish a block-formatting context so a child's top margin
          // (e.g. the preview paper's `margin: spacing(3, "auto")`) is
          // contained instead of collapsing through and leaving a bare
          // `body`-white strip above the branded background.
          display: "flow-root",
          ...containerStyle,
        }}
      >
        {children}
      </div>
    </ThemeProvider>
  );
};

export default BrandedShowLayout;
